import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import ShiftingCountDown from '~/components/SneakerRelease/CountDown';
import { QUERY_GET_RELEASE_PRODUCT } from '~/components/SneakerRelease/graphql';
import SwitchLaunch from '~/components/SneakerRelease/SwitchLaunch';
import sneakerReleaseAtom from '~/services/sneaker-release/sneaker-release.atom';
import { ReleaseType, SneakLaunch, SneakLaunchResponse } from '~/services/sneaker-release/sneaker-release.type';
import { formatNumber } from '~/utils/number';
import styles from './SneakerUpcoming.module.scss';
import { transformReleaseProduct } from '~/services/sneaker-release/sneaker-release-transform';
import { PATH } from '~/constants/enum';

type Props = {
    brandCode?: string;
};
const borderColor = 'border-[#99999980]';

const upcomingNumber = process.env.NEXT_PUBLIC_DAY_NUMBER_UPCOMING ?? 30;

const SneakerUpcoming = ({ brandCode }: Props) => {
    const { releaseType } = useRecoilValue(sneakerReleaseAtom.sneakerRelease);
    const setSneakerRelease = useSetRecoilState(sneakerReleaseAtom.sneakerRelease);
    const sortByReleaseDate = releaseType === ReleaseType.Upcoming ? 'DESC' : 'ASC';
    const upcomingFilters =
        releaseType === ReleaseType.Upcoming
            ? {
                  fromDate: dayjs().format('YYYY-MM-DD'),
                  toDate: dayjs().add(Number(upcomingNumber), 'day').format('YYYY-MM-DD')
              }
            : {};

    const { data: fetchReleaseProduct, loading } = useQuery<SneakLaunchResponse>(QUERY_GET_RELEASE_PRODUCT, {
        variables: {
            page: 1,
            pageSize: 8,
            brand: brandCode,
            releaseDate: sortByReleaseDate,
            ...upcomingFilters
        },
        notifyOnNetworkStatusChange: true
    });
    const [bgActive, setBgActive] = useState<SneakLaunch>();
    const [productBannerActive, setProductBannerActive] = useState<SneakLaunch>();

    const { items = [], totalCount, totalPages } = fetchReleaseProduct?.getReleaseProducts || {};

    const products: SneakLaunch[] = useMemo(() => {
        if (loading) return Array.from({ length: 10 }).map((_, index) => ({ id: index })) as any[];
        const itemsSortByReleaseDate = items?.filter((i) => {
            return sortByReleaseDate === 'ASC'
                ? dayjs(i?.releaseDate).isBefore(dayjs())
                : dayjs(i?.releaseDate).isAfter(dayjs());
        });
        return itemsSortByReleaseDate?.map((i) => transformReleaseProduct(i)) || [];
    }, [loading, items, sortByReleaseDate]);

    useEffect(() => {
        setProductBannerActive(products?.[0]);
    }, [products]);

    const href = productBannerActive?.brandCode
        ? `/${PATH.SneakerReleaseDetail.replace('[brand]', productBannerActive?.brandCode as string).replace(
              '[slug]',
              productBannerActive?.slug as string
          )}`
        : '#';

    return (
        <section className="w-auto grid grid-cols-12 relative md:gap-3 px-2 md:px-8">
            <div className="col-span-7 w-full hidden md:block"></div>
            <div className="lg:col-span-5 col-span-12 flex items-center lg:items-end lg:pb-2 justify-between">
                <div className="flex items-center gap-2 uppercase">
                    <h2
                        className={classNames(
                            releaseType === ReleaseType.Upcoming && 'text-black transition-all',
                            'hidden md:inline-block'
                        )}
                    >
                        Upcoming
                    </h2>
                    <SwitchLaunch
                        onChange={(value: boolean) =>
                            setSneakerRelease((prev: any) => ({
                                ...prev,
                                releaseType: value ? ReleaseType.Upcoming : ReleaseType.Dropped
                            }))
                        }
                        value={releaseType === ReleaseType.Upcoming}
                    />
                    <h2
                        className={classNames(
                            releaseType === ReleaseType.Dropped ? 'text-black transition-all' : 'text-gray-400'
                        )}
                    >
                        Dropped
                    </h2>
                </div>
                {/* <div className="block">
                    <Select value={month} onChange={(value: string) => setMonth(value)} options={monthOptions} />
                </div> */}
            </div>
            <div
                className={`hidden relative md:flex md:col-span-7 transition-colors items-center 
            overflow-hidden ${isEmpty(products) ? 'bg-[#f5f5f5]' : ''}`}
            >
                {products?.map((product, index) => {
                    const active = productBannerActive === product;
                    return (
                        <Link
                            key={index}
                            className={classNames(
                                'position absolute inset-0 lg:p-20 transition-all duration-300 ease-custom-out pointer-events-auto cursor-pointer',
                                active ? 'opacity-1 scale-[1.05]' : 'opacity-0'
                            )}
                            href={href}
                        >
                            <img src={product?.thumbnail?.root} className="object-cover h-full w-full" />
                        </Link>
                    );
                })}
            </div>
            <div
                className={`relative mt-2 col-span-full md:col-span-5 md:aspect-[547/450] md:-mr-2 overflow-auto 
            px-2 scrollbar-cs overscroll-contain ${isEmpty(products) ? 'bg-[#f5f5f5]' : ''}`}
            >
                <ul>
                    {products?.map((product, index) => {
                        const active = bgActive === product;
                        return (
                            <li
                                key={index}
                                onMouseEnter={() => {
                                    setProductBannerActive(product);
                                    setBgActive(product);
                                }}
                                onMouseLeave={() => setBgActive(undefined)}
                                className={index + 1 > 5 ? 'hidden lg:block' : 'block'}
                            >
                                <Link
                                    href={href}
                                    onClick={(e) => loading && e.preventDefault()}
                                    className={`border-b ${borderColor} relative block group py-4 md:pt-2 md:pb-6 group cursor-pointer`}
                                >
                                    <div className="overflow-hidden absolute inset-0">
                                        <div
                                            className={classNames(
                                                'absolute top-0 bottom-0 left-0 z-0 pointer-events-none',
                                                active ? styles.transformIn : styles.transformOut
                                            )}
                                        ></div>
                                    </div>
                                    <article>
                                        <div className="">
                                            <div className="grid grid-cols-12">
                                                <div className={'2xl:col-span-3 col-span-6 hidden lg:block'}>
                                                    <div
                                                        className={classNames(
                                                            styles.releaseDate,
                                                            active && styles.active,
                                                            'flex items-center'
                                                        )}
                                                    >
                                                        <div>
                                                            {loading ? (
                                                                <Skeleton className="h-6 w-20" />
                                                            ) : dayjs(product?.releaseDate).isBefore(dayjs()) ? (
                                                                'Dropped'
                                                            ) : (
                                                                <div className="text-sm">
                                                                    <ShiftingCountDown from={product?.releaseDate} />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                {product?.retail && (
                                                    <div className="col-span-6 text-end hidden lg:block 2xl:hidden ">
                                                        {!!Number(product?.retail)
                                                            ? `$${formatNumber(Number(product?.retail), 2)}`
                                                            : 'Updating'}
                                                    </div>
                                                )}
                                                <div className="2xl:col-span-6 col-span-12">
                                                    <div className="grid grid-cols-12 gap-4">
                                                        <div
                                                            className="col-span-5 flex justify-center items-center md:hidden"
                                                            style={{ backgroundColor: product?.pageTheme }}
                                                        >
                                                            {loading ? (
                                                                <Skeleton
                                                                    className="w-full h-full"
                                                                    containerClassName="w-full h-full"
                                                                />
                                                            ) : (
                                                                <img
                                                                    src={product?.thumbnail?.root}
                                                                    className="w-auto h-[97.5px] sm:h-[112.5px] md:h-[132.5px]"
                                                                />
                                                            )}
                                                        </div>
                                                        <div className="col-span-7 lg:col-span-12 text-sm lg:text-base flex flex-col justify-between">
                                                            <div>
                                                                <p className="lg:text-base text-gray-400">
                                                                    {loading ? (
                                                                        <Skeleton className="h-6" />
                                                                    ) : (
                                                                        product?.brand
                                                                    )}
                                                                </p>
                                                                <p className="lg:text-lg line-clamp-3">
                                                                    {loading ? (
                                                                        <Skeleton className="h-6" />
                                                                    ) : (
                                                                        product?.title
                                                                    )}
                                                                </p>
                                                            </div>
                                                            <div className="flex justify-between text-xs block lg:hidden">
                                                                <div>
                                                                    {loading ? (
                                                                        <Skeleton className="h-6 w-20" />
                                                                    ) : dayjs(product?.releaseDate).isBefore(
                                                                          dayjs()
                                                                      ) ? (
                                                                        'Dropped'
                                                                    ) : (
                                                                        <div>
                                                                            <ShiftingCountDown
                                                                                from={product?.releaseDate}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                {product?.retail && (
                                                                    <div>
                                                                        {Number(product?.retail)
                                                                            ? `$${formatNumber(
                                                                                  Number(product?.retail),
                                                                                  2
                                                                              )}`
                                                                            : 'Updating'}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-span-3 text-end hidden 2xl:block">
                                                    {loading ? (
                                                        <Skeleton className="h-6 max-w-14" />
                                                    ) : !!Number(product?.retail) ? (
                                                        `$${formatNumber(Number(product?.retail), 2)}`
                                                    ) : (
                                                        'Updating'
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </article>
                                </Link>
                            </li>
                        );
                    })}
                </ul>
            </div>
            {isEmpty(products) && (
                <div className="md:absolute top-1/2 left-1/2 md:-translate-y-1/2 md:-translate-x-1/2 col-span-full flex flex-col items-center my-8">
                    <p className="font-semibold text-3xl">Nothing to see here</p>
                    <p>No release found, try again soon.</p>
                </div>
            )}
        </section>
    );
};

export default SneakerUpcoming;
